import React, { useMemo, useRef, useState } from 'react';
import { useStore } from 'effector-react';
import { useQuery } from 'react-query';
import { parseJsonString } from 'pano-utils';
import { useDebounce } from 'use-debounce';
import { type GeneralID, ImageType, SceneType } from '@types';
import { API_DOMAIN, API_SEARCH_DOMAIN, TITLE_LENGTH } from '@constants';
import addComponent from '@utils/addComponent';
import Title from '@components/Title';
import { Collapse } from '@components/Collapse';
import { Validator } from '@components/Validator';
import getTid from '@utils/getTid';
import { sendError } from '@components/Noti';
import {
  $categoryList,
  $categoryMap,
  $sceneMap,
  $tourData,
  $tourDataApi,
} from '@stores/dataStores';
import Textarea from '@components/Textarea';
import ImageContainer from '@components/ImageContainer';
import Image from '@components/Image';
import Tip from '@components/Tip';
import Button from '@components/Button';
import useTourEditing from '@hooks/useTourEditing';
import useTextareaHandelrs from '@hooks/useTextareaHandelrs';
import getPanoViewTrackList from '@utils/getPanoViewTrackList';
import VideoCaptureModal from '@components/VideoCaptureModal';
import { setDocumentTtitle } from '@utils/setDocumentTtitle';
import axios from '@utils/axios';
import { Combobox } from '@headlessui/react';
import Input from '@components/Input';
import { Dropdown } from '@components/Dropdown';
import { uploadThumb } from '../../alioss';
import PanelBase from '../PanelBase';
import { showSceneModal, ThumbReplaceModal } from '../../category';

type SuggestionItem = {
  id: number;
  cnname: string;
};

type ChannelItem = {
  channelId: number;
  channelName: string;
  tags: string[];
  isInput: boolean;
  dictionaryType?: number;
  inputName?: string;
};

const getFirstSceneId = () => {
  const map = $categoryMap.getState();
  const getId = (arr: number[]): GeneralID | undefined => {
    for (let i = 0; i < arr.length; i++) {
      const cateItem = map.get(arr[i]);
      if (cateItem?.sceneIds.length) {
        return cateItem?.sceneIds[0];
      }
      if (cateItem?.categoryIds.length) {
        return getId(cateItem?.categoryIds);
      }
    }
  };
  return getId($categoryList.getState());
};

function PanelTourInfo() {
  const {
    name = '',
    remark = '',
    thumbUrl = '',
    keywords = '',
    channelId,
    subChannel,
    config,
  } = useStore($tourData);
  const { state, setState, isChanged } = useTourEditing({
    name,
    remark,
    thumbUrl,
    keywords,
    channelId,
    subChannel,
    config,
  });
  const { data: channelData = [] } = useQuery('taskChannel', async () => {
    const json = await axios.get<string>(`${API_DOMAIN}/channels/detail/tour`);
    return parseJsonString(json) as ChannelItem[];
  });
  const channel = useMemo(() => {
    return channelData?.find((x) => x.channelId === state.channelId) ?? null;
  }, [channelData, state.channelId]);
  const [searchText] = useDebounce(state.subChannel, 400);
  const [subList, setSubList] = useState<SuggestionItem[]>([]);
  useQuery(
    ['channelSuggest', searchText, channel?.dictionaryType],
    async () => {
      if (!searchText) return [];
      return axios.get<SuggestionItem[]>(
        `${API_SEARCH_DOMAIN}/dictionary/suggest`,
        {
          params: { text: searchText, type: channel?.dictionaryType, size: 8 },
        }
      );
    },
    {
      onSuccess: (list) => {
        setSubList(list);
      },
    }
  );
  const items = useMemo(
    () => channelData.map((x) => ({ id: x.channelId, name: x.channelName })),
    [channelData]
  );

  const titleHandler = useTextareaHandelrs('trim', (val) =>
    setState((draft) => {
      draft.name = val.replace(/\r?\n|\r/g, ' ');
    })
  );
  const remarkHandlers = useTextareaHandelrs('trimRight', (val) =>
    setState((draft) => {
      draft.remark = val;
    })
  );
  const extendDataIDHandlers = (val: string) =>
    setState((draft) => {
      if (draft.config === undefined) {
        draft.config = { extendData: { partnerId: val } };
      }
      if (draft.config.extendData === undefined) {
        draft.config = { extendData: { partnerId: val } };
      } else {
        draft.config.extendData.partnerId = val;
      }
    });
  const keywordsHandlers = useTextareaHandelrs('trimRight', (val) =>
    setState((draft) => {
      draft.keywords = val;
    })
  );
  const channelHandlers = (val: number) => {
    setState((draft) => {
      draft.channelId = val;
      draft.subChannel = '';
    });
  };
  const subChannelHandlers = (val: string) => {
    setState((draft) => {
      draft.subChannel = val;
    });
  };
  const lastIdRef = useRef<GeneralID>();

  const handleConfirm = () => {
    $tourDataApi.update((draft) => {
      draft.name = state.name;
      draft.remark = state.remark;
      draft.thumbUrl = state.thumbUrl;
      draft.keywords = state.keywords;
      draft.channelId = state.channelId;
      draft.subChannel = state.subChannel;
      draft.config = state.config ?? draft.config;
    });
    setDocumentTtitle(state.name);
  };

  const changeThumb = async (file: File) => {
    const url = await uploadThumb({
      file,
      prefix: `/tour/${getTid()}`,
    });
    setState((draft) => {
      draft.thumbUrl = url;
    });
  };

  const handleSceneChange = async () => {
    const { idSet } = await showSceneModal({
      title: '选择场景',
    });
    if (idSet?.size) {
      const id = Array.from(idSet)[0];
      handleSettingClick(id);
    } else {
      handleSettingClick(lastIdRef.current);
    }
  };

  const handleSettingClick = (id?: GeneralID) => {
    lastIdRef.current = id;
    const data = $sceneMap.getState().get(id!);
    if (!data) {
      sendError('获取初始场景错误，请重试');
      return;
    }
    if (data.type === SceneType.pano || data.type === SceneType.matrix) {
      addComponent((remove) => (
        <ThumbReplaceModal
          title="作品封面设置"
          thumbSrc={state.thumbUrl}
          sceneData={data}
          onSceneChangeClick={handleSceneChange}
          onRequestClose={remove}
          onConfirm={changeThumb}
        />
      ));
    } else if (data.type === SceneType.panoVideo) {
      const tracks = getPanoViewTrackList({
        transcodeTrack: data.transcodeTrack,
        cdnDomain: data.cdnDomain,
        originTrack: data.originTrack,
      });
      addComponent((remove) => (
        <VideoCaptureModal
          coverSrc={state.thumbUrl}
          coverType={ImageType.thumb}
          videoUrl={tracks[0].url}
          title="作品封面设置"
          onSceneChangeClick={handleSceneChange}
          onRequestClose={remove}
          onConfirm={changeThumb}
        />
      ));
    }
  };

  return (
    <PanelBase
      isChanged={isChanged}
      onConfirm={handleConfirm}
      title={<Title>作品信息</Title>}
    >
      <Collapse title="作品封面">
        <ImageContainer className="h-115">
          <Image
            className="max-h-full max-w-full"
            type={ImageType.thumb}
            src={state?.thumbUrl}
          />
        </ImageContainer>
        <div className="mt-10 flex items-center justify-between">
          <Tip>建议尺寸：512x512</Tip>
          <Button
            size="sm"
            onClick={() => {
              handleSettingClick(getFirstSceneId());
            }}
          >
            设置封面
          </Button>
        </div>
      </Collapse>
      <Collapse title="作品标题">
        <Validator
          value={state?.name}
          validFn={(val) => !!val.trim()}
          warningMessage="请输入作品标题"
        >
          {({ value, warning }) => (
            <Textarea
              maxLength={TITLE_LENGTH}
              scheme="dark"
              className="h-115"
              placeholder="请输入作品标题"
              value={value}
              warning={warning}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              {...titleHandler}
            />
          )}
        </Validator>
      </Collapse>
      <Collapse title="作品分类">
        <Validator
          value={channel?.channelId}
          validFn={(value) => value !== undefined}
          warningMessage="请选择作品分类"
        >
          {({ warning, value }) => (
            <Dropdown
              disabled={false}
              warning={warning}
              items={items}
              placeholder="请选择"
              selected={value}
              onChange={channelHandlers}
            />
          )}
        </Validator>
        {!!channel?.isInput && (
          <>
            <div className="mt-20">{channel.inputName}</div>
            <Validator
              value={state.subChannel ?? ''}
              validFn={(value) => !!value.trim()}
              warningMessage={`请输入${channel.inputName}`}
            >
              {({ warning, value }) => (
                <Combobox
                  as="div"
                  className="relative mt-10"
                  disabled={false}
                  value={value}
                  onChange={(val) => subChannelHandlers(val ?? '')}
                >
                  <Combobox.Input
                    as={Input}
                    warning={warning}
                    scheme="dark"
                    placeholder={`请输入${channel?.inputName}`}
                    className="h-30 w-full"
                    displayValue={(val: string) => val}
                    onChange={(e) => {
                      subChannelHandlers(e.target.value);
                    }}
                  />

                  {!!subList.length && (
                    <Combobox.Options className="absolute right-0 z-50 w-full pt-5 focus:outline-none">
                      <div className="overflow-hidden rounded bg-white text-black focus:outline-none">
                        {subList.map((x) => (
                          <Combobox.Option
                            key={x.id}
                            className={({ active }) =>
                              `flex h-30 cursor-pointer items-center px-10 ${
                                active && 'bg-white-light'
                              }`
                            }
                            value={x.cnname}
                          >
                            {x.cnname}
                          </Combobox.Option>
                        ))}
                      </div>
                    </Combobox.Options>
                  )}
                </Combobox>
              )}
            </Validator>
          </>
        )}
      </Collapse>
      <Collapse title="作品关键词">
        <Textarea
          scheme="dark"
          className="h-100"
          placeholder="请输入作品关键词，准确的关键词将利于搜索引擎搜索和收录，关键词间使用“,“分割，例：关键词1,关键词2"
          maxLength={500}
          value={state?.keywords}
          {...keywordsHandlers}
        />
      </Collapse>
      <Collapse title="作品简介">
        <Textarea
          scheme="dark"
          className="h-300"
          placeholder="请输入作品简介"
          maxLength={10000}
          value={state?.remark}
          {...remarkHandlers}
        />
      </Collapse>
      <Collapse title="第3方ID">
        <Input
          maxLength={50}
          scheme="dark"
          className="h-30"
          placeholder="请输入第3方平台对应ID"
          value={state.config?.extendData?.partnerId}
          onChange={(e) => extendDataIDHandlers(e.target.value)}
        />
        <div className="mt-10 text-sm text-orange">
          注：通过第3方平台ID（如：房源ID，楼盘ID，项目ID等等）可以更方便地将720漫游作品与第3方平台内容的ID快速关联起来，用于企业用户播放器SDK接入或API接口调用等应用场景
        </div>
      </Collapse>
    </PanelBase>
  );
}

export default PanelTourInfo;
